import { Fragment } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  MarketingShellLayout,
  ValueGrid,
  type ValueItem,
} from "../../components/marketing/MarketingShell";
import { Seo } from "../../seo/Seo";
import { trackCtaClick } from "../../lib/analytics";

const VALUES: ValueItem[] = [
  {
    tag: "CAPTURE",
    title: "Every request, kept.",
    body: "Stripe, GitHub, Linear, Shopify, your own — point them at a Swisstools endpoint. Every payload is stored, indexed, and searchable. 30-day retention free in beta.",
  },
  {
    tag: "INSPECT",
    title: "Headers, body, signature — all of it.",
    body: "See what arrived: full body, parsed JSON, all headers. Signature verification per provider. Search by event type, status code, or payload contents.",
  },
  {
    tag: "REPLAY",
    title: "Bypass to where you are.",
    body: "Tunnel any request to localhost, staging, or a teammate’s laptop. Replay one click. Auto-replay on 5xx with backoff. Production never knows.",
  },
];

const INBOX = [
  {
    src: "stripe",
    evt: "payment_intent.succeeded",
    code: 200,
    ms: "142ms",
    t: "14s",
  },
  {
    src: "github",
    evt: "pull_request.opened",
    code: 200,
    ms: "88ms",
    t: "38s",
  },
  {
    src: "stripe",
    evt: "invoice.payment_failed",
    code: 200,
    ms: "210ms",
    t: "1m",
  },
  { src: "linear", evt: "issue.updated", code: 200, ms: "67ms", t: "2m" },
  { src: "github", evt: "push", code: 500, ms: "—", t: "6m", err: true },
  {
    src: "stripe",
    evt: "customer.subscription.created",
    code: 200,
    ms: "133ms",
    t: "4m",
  },
];

const FLOW = [
  {
    label: "Stripe",
    sub: "webhook source",
    color: "var(--color-signal-lavender)",
  },
  {
    label: "your-project.swisstools.dev",
    sub: "Swisstools inbox — captures, signs, indexes",
    color: "var(--color-ink)",
    wide: true,
  },
  {
    label: "api.domain.com",
    sub: "your real API endpoint, wherever it lives",
    color: "var(--color-signal-ok)",
    wide: true,
  },
];

export function WebhooksMarketingPage() {
  return (
    <MarketingShellLayout active="Webhooks">
      <Seo route="/webhooks" />
      {/* Hero */}
      <section>
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 pt-12 sm:pt-16 pb-10 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-14 items-start">
          <div>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] uppercase text-signal-lavender">
              <span className="size-1.5 rounded-sm bg-signal-lavender" />
              04 · Webhook inbox · for the debugging side of integrating
            </span>
            <h1 className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-semibold leading-[0.96] tracking-[-0.04em]">
              Every payload,
              <br />
              <span className="font-serif italic font-normal">
                inspectable.
              </span>
              <br />
              <span className="text-signal-lavender">Replayable.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl font-serif">
              Point your provider at a Swisstools URL. See every request —
              body, headers, signature, latency. Replay any of them, or bypass
              live traffic to a tunnel of your choice.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 items-center">
              <Link
                to="/register"
                onClick={() =>
                  trackCtaClick("hero", {
                    target: "register",
                    surface: "Webhooks",
                  })
                }
              >
                <Button size="lg">Start free →</Button>
              </Link>
              <a
                href="/#playground"
                onClick={() =>
                  trackCtaClick("sub_nav", { surface: "Webhooks" })
                }
                className="text-sm font-medium underline underline-offset-4 decoration-rule hover:decoration-foreground"
              >
                Try the live demo →
              </a>
            </div>
          </div>

          {/* Hero — inbox preview */}
          <div className="self-start w-full">
            <div className="bg-card-paper border border-rule rounded-xl overflow-hidden shadow-[0_18px_42px_-22px_rgba(27,31,42,0.18)]">
              <div className="px-3.5 py-2.5 border-b border-rule-soft bg-panel flex justify-between items-center">
                <span className="font-mono text-[10.5px] text-muted-foreground tracking-[0.1em] uppercase">
                  Inbox · last 24h
                </span>
                <span className="font-mono text-[10px] text-signal-lavender">
                  ● 2,841 events
                </span>
              </div>
              <div>
                {INBOX.map((r, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[52px_72px_1fr_56px_42px] gap-2 px-3.5 py-2.5 border-b border-rule-soft items-center text-xs"
                  >
                    <span className="font-mono text-[9.5px] font-bold text-signal-ok">
                      POST
                    </span>
                    <span className="font-mono text-[11px]">{r.src}</span>
                    <span className="font-mono text-[11px] text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap">
                      {r.evt}
                    </span>
                    <span
                      className={`font-mono text-[10.5px] px-1.5 py-px rounded font-bold justify-self-start ${
                        r.err
                          ? "bg-signal-red-soft text-signal-red"
                          : "bg-signal-ok-soft text-signal-ok"
                      }`}
                    >
                      {r.code}
                    </span>
                    <span className="font-mono text-[10.5px] text-faint text-right">
                      {r.t}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ValueGrid items={VALUES} accent="var(--color-signal-lavender)" />

      {/* Diagram — how it flows */}
      <section className="border-t border-rule bg-card-paper">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-10 sm:py-12">
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-faint mb-5">
            How it flows
          </div>
          <div className="flex flex-col md:flex-row md:items-stretch gap-4 md:gap-4">
            {FLOW.map((n, i) => (
              <Fragment key={n.label}>
                <div
                  className={`flex-1 p-4 sm:p-5 bg-paper border border-rule rounded-xl ${
                    n.wide ? "md:flex-[1.4]" : ""
                  }`}
                >
                  <div
                    className="size-2 rounded-sm mb-2"
                    style={{ background: n.color }}
                  />
                  <div className="font-mono text-[13px] font-semibold mb-1">
                    {n.label}
                  </div>
                  <div className="font-serif italic text-[12.5px] text-muted-foreground leading-relaxed">
                    {n.sub}
                  </div>
                </div>
                {i < FLOW.length - 1 && (
                  <span className="self-center font-mono text-xl text-faint rotate-90 md:rotate-0">
                    →
                  </span>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </section>
    </MarketingShellLayout>
  );
}
